import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  AvatarGroup,
  Avatar,
} from '@mui/material';
import { Plus, X } from 'lucide-react';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { showToast } from '../../store/uiSlice';
import { boardsApi } from '../../api/endpoints/boardsApi';
import { fetchWorkspaceTeams } from '../../store/slices/teamsSlice';

interface BoardTeamsPanelProps {
  boardId: string;
  workspaceId?: string;
  canManage: boolean;
}

interface AssignedTeam {
  id: number;
  name: string;
  members: { id: number; fullName: string; userName: string }[];
}

const BoardTeamsPanel: React.FC<BoardTeamsPanelProps> = ({ boardId, workspaceId, canManage }) => {
  const dispatch = useAppDispatch();
  const { teamsByWorkspace } = useAppSelector((state) => state.teams);
  const workspaceTeams = (workspaceId && teamsByWorkspace[workspaceId]) || [];

  const [assignedTeams, setAssignedTeams] = useState<AssignedTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const load = () => {
    setLoading(true);
    boardsApi
      .getBoardAccess(boardId)
      .then((access) => setAssignedTeams(access.teams || []))
      .catch((error) => console.error('Failed to load project teams:', error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (boardId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);

  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchWorkspaceTeams(workspaceId));
    }
  }, [dispatch, workspaceId]);

  const assignedTeamIds = new Set(assignedTeams.map((t) => String(t.id)));
  const availableToAssign = workspaceTeams.filter((t) => !assignedTeamIds.has(String(t.id)));

  const handleAssign = async () => {
    if (!selectedTeamId) return;
    setAssigning(true);
    try {
      await boardsApi.assignTeam(boardId, Number(selectedTeamId));
      dispatch(showToast({ message: 'Team assigned to project', type: 'success' }));
      setSelectedTeamId('');
      load();
    } catch (error: any) {
      const status = error?.response?.status;
      dispatch(
        showToast({
          message:
            status === 409
              ? 'This team is already assigned to this project'
              : error?.response?.data?.message || error?.message || 'Failed to assign team',
          type: 'error',
        }),
      );
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async (teamId: number) => {
    try {
      await boardsApi.unassignTeam(boardId, teamId);
      dispatch(showToast({ message: 'Team removed from project', type: 'success' }));
      load();
    } catch (error: any) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || error?.message || 'Failed to remove team',
          type: 'error',
        }),
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {canManage && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel shrink>Assign team</InputLabel>
            <Select
              label="Assign team"
              value={selectedTeamId}
              displayEmpty
              onChange={(e) => setSelectedTeamId(e.target.value as string)}
            >
              {availableToAssign.length === 0 && (
                <MenuItem value="" disabled>
                  {workspaceTeams.length === 0
                    ? 'No teams exist in this workspace yet'
                    : 'All teams are already assigned'}
                </MenuItem>
              )}
              {availableToAssign.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            color="primary"
            disabled={!selectedTeamId || assigning}
            onClick={handleAssign}
            aria-label="Confirm assign team"
          >
            <Plus size={18} />
          </IconButton>
        </Box>
      )}

      <List dense>
        {assignedTeams.map((team) => (
          <ListItem
            key={team.id}
            secondaryAction={
              canManage && (
                <IconButton
                  size="small"
                  onClick={() => handleUnassign(team.id)}
                  aria-label="Unassign team"
                >
                  <X size={16} />
                </IconButton>
              )
            }
          >
            <ListItemText
              primary={team.name}
              secondary={`${team.members.length} member${team.members.length === 1 ? '' : 's'}`}
            />
            <AvatarGroup max={3} sx={{ mr: 1 }}>
              {team.members.map((m) => (
                <Avatar key={m.id} sx={{ width: 24, height: 24, fontSize: 11 }}>
                  {(m.fullName || m.userName || '?').charAt(0).toUpperCase()}
                </Avatar>
              ))}
            </AvatarGroup>
          </ListItem>
        ))}
      </List>

      {assignedTeams.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No teams assigned to this project yet.
        </Typography>
      )}
    </Box>
  );
};

export default BoardTeamsPanel;
